'use client'
import {useUiControlLabels} from "@/components/i18n/ui-control-labels-context"
import { useCreditFormatters } from "@/hooks/use-credit-formatters"


import { useEffect, useState, useCallback } from 'react'
import type { DailyUsage } from '@/lib/billing/types'

import { useAuth } from '@/hooks/use-auth'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Loader2, BarChart3 } from 'lucide-react'
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts'
import { useTranslations } from "next-intl"



/** Text split into the agents' own requests and Conversation Quality's, stacked in one bar. */
interface ChartDay extends DailyUsage {
  agent_count: number
  agent_cost: number
}

export function UsageChart() {
  const {locale: uiDateLocale} = useUiControlLabels()
  const tUi = useTranslations("Billing.BillingUsageChart")

  const { fetchWithAuth } = useAuth()
  const [data, setData] = useState<ChartDay[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(false)
      const res = await fetchWithAuth('/api/billing/usage-daily?days=30')
      if (!res.ok) { setError(true); return }
      const json = (await res.json()) as { days?: DailyUsage[] }
      setData(
        (json.days ?? []).map((day) => ({
          ...day,
          agent_count: day.text_count - day.quality_count,
          agent_cost: day.text_cost - day.quality_cost,
        })),
      )
    } catch {
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [fetchWithAuth])

  useEffect(() => { fetchData() }, [fetchData])

  const hasData = data.some((d) => d.text_count > 0 || d.voice_count > 0)
  // Only charges the tenant paid: a platform-paid quality call leaves none, so this stays off for it.
  const hasQuality = data.some((d) => d.quality_count > 0)

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-1">
          <BarChart3 className="h-4 w-4" /> <span><span key={tUi("usageLast30Days")}>{tUi("usageLast30Days")}</span></span>
        </CardTitle>
        <CardDescription>
          <span key={tUi("dailyTextAndVoiceUsageBreakdown")}>{tUi("dailyTextAndVoiceUsageBreakdown")}</span></CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : null}
        {!loading && error ? <p className="text-sm text-muted-foreground py-4 text-center"><span key={tUi("unableToLoadUsageData")}>{tUi("unableToLoadUsageData")}</span></p> : null}
        {!loading && !error && !hasData ? <p className="text-sm text-muted-foreground py-4 text-center"><span key={tUi("noUsageRecordedYet")}>{tUi("noUsageRecordedYet")}</span></p> : null}
        {!loading && !error && hasData ? (
          <div className="h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} barGap={0} barCategoryGap="20%">
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(value: string) => formatShortDate(value, uiDateLocale)}
                  tick={{ fontSize: 11 }}
                  interval="preserveStartEnd"
                  className="text-muted-foreground"
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  className="text-muted-foreground"
                  allowDecimals={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  wrapperStyle={{ fontSize: 12 }}
                  iconType="circle"
                  iconSize={8}
                />
                {hasQuality ? (
                  <>
                    <Bar dataKey="agent_count" name="Text Requests" stackId="text" fill="#3b82f6" />
                    <Bar
                      dataKey="quality_count"
                      name="Conversation Quality"
                      stackId="text"
                      fill="#c4b5fd"
                      stroke="hsl(var(--background))"
                      strokeWidth={1}
                      radius={[2, 2, 0, 0]}
                    />
                  </>
                ) : (
                  <Bar dataKey="text_count" name="Text Requests" fill="#3b82f6" radius={[2, 2, 0, 0]} />
                )}
                <Bar dataKey="voice_count" name="Voice Calls" fill="#10b981" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}

function formatShortDate(dateStr: string, dateLocale: string = "en-US"): string {
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString(dateLocale, { month: 'short', day: 'numeric' })
}

function CustomTooltip({ active, payload, label }: {
  active?: boolean
  payload?: { payload?: ChartDay }[]
  label?: string | number
}) {
  const {locale: uiDateLocale} = useUiControlLabels()

  const { formatCredits } = useCreditFormatters()

  const tUi = useTranslations("Billing.BillingUsageChart")

  if (!active || !payload?.length) return null
  const day = payload[0]?.payload
  if (!day) return null

  return (
    <div className="rounded-lg border bg-background p-3 shadow-md text-sm">
      <p className="font-medium mb-1">{formatShortDate(String(label), uiDateLocale)}</p>
      <div className="space-y-0.5 text-xs">
        <p className="text-blue-600">{<span key={tUi("textValue0RequestsValue1", { value0: String(day.agent_count), value1: String(formatCredits(day.agent_cost)) })}>{tUi("textValue0RequestsValue1", { value0: String(day.agent_count), value1: String(formatCredits(day.agent_cost)) })}</span>}</p>
        {day.quality_count > 0 && (
          <p className="text-violet-600">
            {<span key={tUi("conversationQualityValue0RequestsValue1", { value0: String(day.quality_count), value1: String(formatCredits(day.quality_cost)) })}>{tUi("conversationQualityValue0RequestsValue1", { value0: String(day.quality_count), value1: String(formatCredits(day.quality_cost)) })}</span>}
          </p>
        )}
        <p className="text-emerald-600">{<span key={tUi("voiceValue0CallsValue1", { value0: String(day.voice_count), value1: String(formatCredits(day.voice_cost)) })}>{tUi("voiceValue0CallsValue1", { value0: String(day.voice_count), value1: String(formatCredits(day.voice_cost)) })}</span>}</p>
        <p className="text-muted-foreground font-medium mt-1">{<span key={tUi("totalValue0", { value0: String(formatCredits(day.text_cost + day.voice_cost)) })}>{tUi("totalValue0", { value0: String(formatCredits(day.text_cost + day.voice_cost)) })}</span>}</p>
      </div>
    </div>
  )
}
